import React, { useRef, useEffect, useState } from "react";
import Editor from "@monaco-editor/react";
import { Select, Tooltip } from "antd";
import ts from "typescript";
import * as _ from "lodash";

import { useToast } from "../helpers/commonFunction";
import { Scope } from '../helpers/typedefinitions/interface'
import { generateTypeDefs } from './typedefinitions'
import TarsIcon from '../assets/images/tarsIcon.png'
import TarsModal from "./TarsModal";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../stateManager/store";
import { setStepIndex } from "../stateManager/store/onboarding/action";

const { Option } = Select;

interface IProps {
  json?: any;
  value?: any;
  editor?: any;
  height?: string;
  onSave?: ((data: any, modalId: boolean) => void) | null;
  theme?: string;
  type?: Scope;
  fontSize?: number;
  minimap?: boolean;
  lang: "javascript" | "typescript" | "json";
  lineNumbers?: "on" | "off";
  readOnly?: boolean;
  customdef?: string;
  editRangePhrase?: {
    start: string;
    end: string;
  };
  saveMessage?:
  | {
    enable: true;
    message: string;
  }
  | {
    enable: false;
  };
  isLangSelVisible?: boolean;
  additionalOptions?: any;
  editorRef?: any;
  currentLine?:any;
  setCurrentLine?:any;
  visibleTars?:boolean;
  tarsModal?:boolean;
  setTarsModal?:any;
  width?:string;
  tarsVisible?:boolean;
  functionPreviewModal?:boolean;
}

export default React.memo((props: IProps) => {
  const {
    isLangSelVisible = true,
    additionalOptions = {},
    currentLine,
    setCurrentLine,
    visibleTars = false,
    tarsModal = false,
    setTarsModal,
    width = "100%",
    tarsVisible = false,
    functionPreviewModal= false
  } = props
  const [lang, setLang] = useState("javascript");
  const monacoRef: any = useRef(null)

  const { guideVisible, info } = useSelector((state: AppState) => state.onboarding)

  // To give control to components which uses this component
  // Eg: accessing and setting the editor instance value
  const instanceRef: any = useRef(null);
  const editorRef: any = props.editorRef ? props.editorRef : instanceRef;

  const dispatch  = useDispatch()

  // Phrases which define editable areas
  const startPhrase = (props.editRangePhrase && props.editRangePhrase.start) || "// Start of editable area";
  const endPhrase = (props.editRangePhrase && props.editRangePhrase.end) || "// End of editable area";

  const editableArea = (() => {
    return {
      includes: function (changes: any, model: any) {
        const fullRange = model.getFullModelRange();
        let { range: startRange } = model.findMatches(startPhrase, fullRange).shift() || {};
        let { range: endRange } = model.findMatches(endPhrase, fullRange).pop() || {};
        const { startLineNumber, endLineNumber, startColumn, endColumn } = fullRange;
        if (startRange && endRange) {
          startRange = startRange.setStartPosition(startLineNumber, startColumn);
          endRange = endRange.setEndPosition(endLineNumber, endColumn);
          endRange = endRange.setStartPosition(endRange.startLineNumber, 1);
          return changes.every(({ range, forceMoveMarkers }: any,) => {
            //forceMoveMarkers will be true when formatting happens
            return (forceMoveMarkers || ((range.startLineNumber > startRange.endLineNumber) && (range.startLineNumber < endRange.startLineNumber)))
          });
        }
        return false;
      },
    };
  })();

  function beforeMount(monacoInstance: any) {
    monacoRef.current = monacoInstance
    monacoInstance.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
      noSuggestionDiagnostics: false,
    });
    const options = {
      lib: ['esnext'],
      target: monacoInstance.languages.typescript.ScriptTarget.ES2016,
      allowNonTsExtensions: true,
      noImplicitAny: false,
      noUnusedParameters: false,
      // noLib: true
    };
    if(props.type === 'inline' || props.type === 'functionClient') {
      options.lib.push('dom')      
    }
    // compiler options
    monacoInstance.languages.typescript.javascriptDefaults.setCompilerOptions(options);
    monacoInstance.languages.typescript.typescriptDefaults.setCompilerOptions(options);
    // extra libraries
    var libSource = [];

    // Getting dynamic function typedefs here
    if (props.customdef) {
      libSource.push(props.customdef);
    }

    // Generate static typedefs
    let typedefs = ""
    if (props.type) {
      typedefs = generateTypeDefs(props.type);
    }
    libSource.push(typedefs)
    var libUri = "ts:customdefination/index.d.ts";
    monacoInstance.languages.typescript.javascriptDefaults.addExtraLib(libSource.join("\n"), libUri);
    const model = monacoInstance.editor.getModels();
    const find = _.find(model, (o) => {
      return o.getModeId() === "typescript";
    });
    if (!find) {
      monacoInstance.editor.createModel(libSource.join("\n"), "typescript", monacoInstance.Uri.parse(libUri));
    } else {
      monacoInstance.languages.typescript.typescriptDefaults.addExtraLib(libSource.join("\n"), libUri);
    }
  }

  function editorAreaCorrection(editor:any) {
    const lines: string[] = editorRef.current.getValue().split("\n");

    const endPhraseIndex = lines.findIndex((line) => line.trim() === endPhrase);
    if (endPhraseIndex === -1) {
      return;
    }

    let funcEndIndex = lines
      .slice(endPhraseIndex)
      .findIndex((line) => line.trim() === "}");
    if (funcEndIndex === -1) {
      return;
    }
    funcEndIndex += endPhraseIndex;

    const hasContent =
      lines.slice(funcEndIndex + 1).findIndex((line) => line.trim()) !== -1;
    if (!hasContent) {
      return;
    }

    const value = [
      ...lines.slice(0, endPhraseIndex),
      ...lines.slice(funcEndIndex + 1),
      ...lines.slice(endPhraseIndex, funcEndIndex + 1),
    ].join("\n");

    editor.getModel().setValue(value);
    editor.getAction("editor.action.formatDocument").run();
  }

  function handleEditorDidMount(editor: any) {
    editorRef.current = editor;
    const editorModel = editor.getModel();
    editorAreaCorrection(editor)

   if (guideVisible) {
    editor.getContainerDomNode().addEventListener('paste', (event: any) => {
      let pasteVal = event.clipboardData?.getData("text");
      let val = editor.getValue();   
      pasteVal && editor.setValue(val.split(pasteVal).join(pasteVal.split("//").join("").split(';').join('\n')))
      event.preventDefault();
      event.stopPropagation();
    });
   }
    
    editor.onDidChangeCursorPosition((event:any) => {
      if (setCurrentLine){
        if(event.position.lineNumber < 2){
          setCurrentLine(3);
        }else{
          setCurrentLine(event.position.lineNumber);
        }
      }
    });
    editorModel.onDidChangeContentFast(({ changes, isUndoing }: { changes: any; isUndoing: any }) => {
      if (!isUndoing) {
        if (!editableArea.includes(changes, editorModel)) {
          /*
           * This Promise.resolve() sends the code to the micro task queue
           * And it gets called before the event queue ( micro task queue has more priority than event queue)
           * Thus, Promise.resolve() will be better than a setTimeout(fn,0) here
           * If we do it synchronously, it affects some of monaco editor's functions
           */
          Promise.resolve().then(() => editor.trigger("someIdString", "undo"));
        }
      }
    });
    
    editor.onDidChangeModelContent(() => {
        editorAreaCorrection(editor)
    });
  }

  const { ErrorToast, SuccessToast } = useToast()

  const { onSave, readOnly, saveMessage } = props;

  useEffect(() => {
    if (props.lang) setLang(props.lang);
  }, [props.lang]);

  // useEffect(() => {
  //   // if(monoInstance)
  //   // loadDefination()
  // }, [monoInstance]);

  // const saveMonoInstance = async () => {
  //   try {
  //     let monacoIn = await monaco.init();
  //     setmonoInstance(monacoIn);
  //   } catch (e) {
  //   }
  // };
  // useEffect(() => {
  //   if (monoInstance) {
  //     let monaco = monoInstance;
  //     // validation settings
  //     monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
  //       noSemanticValidation: false,
  //       noSyntaxValidation: false,
  //       noSuggestionDiagnostics: false,
  //     });
  //     const options = {
  //       // lib: ['ES2015', "DOM"],
  //       target: monaco.languages.typescript.ScriptTarget.ES2016,
  //       allowNonTsExtensions: true,
  //       noImplicitAny: false,
  //       noUnusedParameters: false,
  //       // noLib: true
  //     };
  //     // compiler options
  //     monaco.languages.typescript.javascriptDefaults.setCompilerOptions(options);
  //     monaco.languages.typescript.typescriptDefaults.setCompilerOptions(options);
  //     // extra libraries
  //     var libSource = ["", "declare var moment: any;", "declare var _: any;", "declare var axios: any;", a8flowDefs, a8dataTypeDef, loggingTypeDefs];
  //     libSource.push(a8configTypeDef);
  //     if (props.type === "func") libSource.push(funcVaraible);
  //     if (props.type === "service") libSource.push(Variables);
  //     if (props.type === "property")
  //       libSource.push(
  //         `interface IValueInfo {
  //           filename?: string,
  //           mimetype?: string
  //          };
  //         declare var bindParams: any; declare function setBindParams(key:string, value: any, valueInfo?: IValueInfo, update?: boolean ): void;`
  //       );
  //     if (props.customdef) libSource.push(props.customdef);
  //     else libSource.push("declare var a8func: any;");
  //     var libUri = "ts:customdefination/index.d.ts";
  //     monaco.languages.typescript.javascriptDefaults.addExtraLib(libSource.join("\n"), libUri);
  //     const model = monaco.editor.getModels();
  //     const find = _.find(model, (o) => {
  //       return o.getModeId() === "typescript";
  //     });
  //     if (!find) {
  //       monaco.editor.createModel(libSource.join("\n"), "typescript", monaco.Uri.parse(libUri));
  //     } else {
  //       monaco.languages.typescript.typescriptDefaults.addExtraLib(libSource.join("\n"), libUri);
  //     }
  //   }
  // }, [monoInstance, props.customdef, props.type]);
  useEffect(() => {
    if (onSave && !readOnly) {
      const keydownHandler = async (e: any) => {
        if ((e.key === "s" || e.key === "S") && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();

          let source = editorRef.current.getValue();
          let compileSource;
          //Format code
          await editorRef.current.getAction('editor.action.formatDocument').run()

          let errors = monacoRef.current.editor.getModelMarkers({}).filter((d: any) => d.resource.path !== "customdefination/index.d.ts" && d.code !== "7044" && d.code !== "6133");

          if (errors.length > 0) {
            const message = errors[0].message;
            ErrorToast(message ? message : "Validation failed")
            e.preventDefault();
            if (guideVisible) {
              dispatch(setStepIndex(info.stepIndex - 1));
            }
            return 0;
          }
          if (lang === "typescript" || lang === "javascript") {
            compileSource = ts.transpileModule(source, {
              compilerOptions: {
                target: ts.ScriptTarget.ES2016,
                module: ts.ModuleKind.CommonJS,
                noEmit: true,
              },
            });
            if (compileSource.outputText) {
              const code = compileSource.outputText;
              const funIndex = code.indexOf("function main(");
              if (funIndex < 0) {
                ErrorToast("Main function not found")
                e.preventDefault();
                return 0;
              }
              compileSource = `async function(...params) {
                                ${code}
                               return main(...params)
                            }`;
            }
          }
          // compileSource = source;
          // }
          !guideVisible && saveMessage &&
            saveMessage.enable &&
            SuccessToast(saveMessage.message)

          const model = editorRef.current.getModel()

          let modalId: number = 7238212;

          onSave({ source: model.getValueInRange(model.getFullModelRange()), comSource: compileSource }, e.detail === modalId);
        }
      };
      // if (count === 0) {
      //   setcount(1);
      //   saveMonoInstance();
      //   monaco
      //     .init()
      //     .then((monaco) => {})
      // }
      document.addEventListener("keydown", keydownHandler);
      return () => {
        document.removeEventListener("keydown", keydownHandler);
      };
    }
  }, [onSave, SuccessToast, ErrorToast, lang, readOnly, saveMessage, props.type, props.customdef, editorRef]);
 
  const getEditorHeight = () => {
    let height = functionPreviewModal ? "calc(100vh - 230px)" : "calc(100vh - 135px)";
    return height;
  };

  const onRowSelect = (()=>{
    setTarsModal(true)
  })

  return (
    <div style={{ height: "100%" }}>
      {lang && isLangSelVisible && (
        <div
          className={"row"}
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding:"10px 10px 0px 10px"
          }}
        >
        </div>
      )}
        <Editor
          data-test-id="editor"
          height={getEditorHeight()}
          options={{
            minimap: {
              enabled: props.minimap || false,
            },
            fontSize: props.fontSize,
            lineNumbers: props.lineNumbers || "on",
            scrollBeyondLastLine: false,
            readOnly: readOnly ? readOnly : false,
            renderLineHighlight: "none",
            ...additionalOptions,
          }}
          value={props.value}
          language={lang}
          theme={props.theme || "light"}
          onMount={handleEditorDidMount}
          beforeMount={beforeMount}
          keepCurrentModel={false}
          width={width}
        />
    </div>
  );
});
